// ABOUTME: TypeScript interfaces and types for BePono API communication including users, children, goals, and behaviors
// ABOUTME: Comprehensive type definitions supporting authentication, family management, goal tracking, and multi-child operations

/**
 * User account information
 * Represents an authenticated user in the BePono system
 */
export interface User {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl?: string;
  isAdmin?: boolean;
  role?: string;
  mantra?: string;
  familyLoveQuestion?: string;
  familyNeedQuestion?: string;
  emailVerificationStatus?: string; // "Verified", "NotVerified", "VerificationSkipped"
  createdAt?: string; // ISO 8601 string
}

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string;
  password: string;
  // Issue #122 - Browser timezone for daily reset calculations
  browserTimezone?: string;
}

/**
 * User registration request payload
 */
export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/**
 * NextAction types for routing after authentication
 */
export type NextActionType = 'dashboard' | 'family_setup' | 'invite_acceptance' | 'account_status';

/**
 * Authentication response with JWT token
 * Returned on successful login/registration
 */
export interface AuthResponse {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl?: string;
  isAdmin: boolean;
  token: string; // JWT token for API authentication
  refreshToken?: string; // Refresh token for sliding sessions (7-day expiration)
  refreshTokenExpires?: string; // ISO string of refresh token expiration
  nextAction?: NextActionType; // Where to route the user after auth
  hasFamily?: boolean; // Whether user has an active family
  emailVerificationStatus?: string; // "Verified", "NotVerified", "VerificationSkipped"
  createdAt?: string; // ISO 8601 string
}

/**
 * Refresh token request for sliding sessions
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh token response — body contains only the new access token.
 * The rotated refresh token is set in an HttpOnly cookie by the server.
 */
export interface RefreshTokenResponse {
  accessToken: string;
  accessTokenExpires: string;
}

export interface Family {
  familyId: number;
  name: string;
  description?: string;
  timezone?: string;
  ownerId: number;
  inviteCode: string;
  createdAt: string;
  effectiveDailyLoggingGoalPerChild?: number;
  effectiveThemeName?: string;
  effectiveLabelLanguageMode?: string;
}

export interface FamilySummary {
  id: number;
  name: string;
  memberCount: number;
  childrenCount: number;
  userRole: number;
}

export interface Child {
  childId?: number; // API might return 'id' instead
  id?: number;
  familyId?: number;
  name: string;
  aliases?: string[];
  birthYear: number;
  birthMonth?: number;
  birthDay?: number;
  dateOfBirth?: string; // For backwards compatibility
  profileImageUrl?: string;
  isActive?: boolean;
  createdAt: string;
  age?: number;
  gender?: string;
  mantra?: string;
  familyLoveQuestion?: string;
  familyNeedQuestion?: string;
  familyTimezone?: string; // Family timezone for Timeline Journey Chart
  todaysBehaviors?: BehaviorRecord[]; // Today's behaviors for timeline chart
}

/**
 * The three types of behaviors in the BePono system
 * 
 * CORE BEPONO METHODOLOGY:
 * - Star (1): Positive behavior - moves child up the ladder
 * - Triangle (2): Warning behavior - neutral or slight penalty
 * - Square (3): Consequence behavior - significant penalty, resets ladder
 */
export enum BehaviorType {
  Star = 1,        // ⭐ Good behavior - move up 1 step
  Triangle = 2,    // ⚠️ Minor issue - move down 1 step
  Square = 3,      // 🛑 Meltdown/tantrum - drop to timeout + no heart
  FlipYourCoin = 4, // 🪙 Emotional regulation recovery - no ladder movement
  MakeItRight = 5,  // 🤝 Parent acknowledgment that cancels out squares for heart calculations
  OneOnOneTime = 6, // 🕐 Parent-child quality time tracking - no ladder movement
  GoalNuke = 7,    // ☢️ Nuclear reset - fails all active non-exempt goals and recreates them (Issue #1310)
  Reset = 8        // 🌀 Child chooses to process without consequence (Crossing) - neutral: no counts, heart, or ladder (Issue #1937)
}

/**
 * How a behavior record was created. Mirrors BehaviorRecordingSource in
 * BePono.API/Models/BehaviorRecord.cs.
 */
export enum BehaviorRecordingSource {
  Unknown = 0,                  // Existing records before source tracking
  WebApplicationButton = 1,     // Dashboard buttons
  Alexa = 2,                    // Alexa skill integration
  WebApplicationVoice = 3,      // Voice recording in web app
  Siri = 4,                     // Siri integration (future)
  ChoreCompletion = 5,          // Auto-awarded when chore list is completed
  CompletedMakeItRightList = 6, // Auto-recorded when Make It Right list is approved
  LadderBottomAutoSquare = 7,   // Auto-created when triangle drops child to StopAndReset (Issue #1441)
  ChildDevice = 8               // Child-initiated from their own device (e.g. a Reset crossing) - no recording user (Issue #1937)
}

export interface BehaviorRecord {
  behaviorId: number;
  childId: number;
  behaviorType: BehaviorType;
  notes?: string;
  recordedAt: string;
  recordedByUserId: number;
  recordedByUserName?: string;
  source?: number;             // BehaviorRecordingSource enum value (matches API response)
  sourceDisplayName?: string;  // Human-readable source name (matches API response)

  // Enhanced time storage (Issue #312) - ensures timeline stability
  eventTime?: string;          // UTC time for sorting/filtering
  eventTimezone?: string;      // Timezone when behavior was recorded
  eventLocalTime?: string;     // UTC equivalent of what user originally saw
}

/**
 * Guided post-Square/Reset crossing (Issue #1937).
 * Mirrors the C# Crossing models; enum fields arrive as numbers over the wire.
 */
export enum CrossingTrigger {
  Square = 1,        // Auto-created from a logged Square - parent-led
  ChildInitiated = 2 // Started by the child's Reset control - child-solo
}

export enum CrossingStatus {
  NotStarted = 0,
  InProgress = 1,
  Complete = 2, // terminal
  Expired = 3   // terminal - closed by the 3am reconciliation
}

export enum RepairStatus {
  Pending = 0,             // default; also an Expired crossing that ended unresolved
  Complete = 1,            // mend done
  CompleteForgiven = 2,    // receiver forgave
  CompleteNoMendNeeded = 3 // nothing needed mending (no reaches)
}

/**
 * Lightweight crossing summary for list views.
 * status/trigger/repairStatus are numeric enums over the wire.
 */
export interface CrossingListItem {
  id: number;
  status: CrossingStatus;
  trigger: CrossingTrigger;
  triggerBehaviorType: BehaviorType; // BehaviorType of the anchoring record (Square or Reset)
  currentScreenId: string; // the screen the crossing is on, presented clean (a null or removed pointer reads as the first screen)
  repairStatus: RepairStatus;
  createdAt: string;
  completedAt?: string;
}

export interface CrossingReach {
  id: number;
  crossingId: number;
  reachCode: string;
  mendCompleted: boolean;
  completedAt?: string;
}

export interface CrossingMend {
  id: number;
  crossingId: number;
  reachCode?: string;
  optionCode?: string;
  labelSnapshot: string;
  completedAt?: string;
}

export interface CrossingTooMuchSelection {
  id: number;
  crossingId: number;
  categoryCode: string;
  supports: string[];
}

/**
 * Full crossing detail including child-owned answers and child collections.
 */
export interface CrossingHoldListRow {
  id: number;
  title: string;
  completedItems: number;
  totalItems: number;
}

// What Hold draws: the Make It Right list as Kuleana shows it, and up to three of the child's other active lists.
export interface CrossingMakeItRightHold {
  tile: ChoreListPeriodStatus;
  otherLists: CrossingHoldListRow[];
}

export interface CrossingDto {
  id: number;
  childId: number;
  childName: string;
  // The child's 1-based position among all of that child's crossings (any trigger, any status) ordered by
  // CreatedAt, then Id; never the database id.
  crossingNumber: number;
  familyId: number;
  behaviorRecordId: number;
  trigger: CrossingTrigger;
  status: CrossingStatus;
  darkCreated: boolean;
  familyDate: string; // DateOnly (YYYY-MM-DD)
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  expiredAt?: string;
  breathSecondsTotal: number;
  breathRoundCount: number;
  feeling?: string;
  weather?: string;
  storm?: string;
  receiverAnswer?: string;
  kala?: string;
  repairStatus: RepairStatus;
  ruleAccepted?: boolean;
  reaches: CrossingReach[];
  mends: CrossingMend[];
  tooMuchCategories: CrossingTooMuchSelection[];
  // The crossing's Make It Right list (soft-deleted lists included), or absent. The Make It Right screen reads it.
  makeItRightListId?: number | null;
  // Present when the crossing has a live (not soft-deleted) Make It Right list. The Hold screen reads it.
  makeItRightHold?: CrossingMakeItRightHold | null;
  // Present on every single-crossing response (GET and the action call), never on list items. Use
  // CrossingWithProgress where the runner needs it guaranteed.
  progress?: CrossingProgress;
}

/**
 * The crossing runner contract (Issue #2053; runner-architecture.md sections 3 and 4.1).
 * Unlike the numeric enums above, these arrive as snake_case strings (the server's StringEnumConverter);
 * pill states are literal kebab-case strings. The unions are written from runner/__fixtures__/definition.json.
 */
export type CrossingScene = 'breath' | 'ocean' | 'clouds' | 'sky';
export type CrossingScreenKind = 'screen' | 'overlay_scene' | 'overlay_page';
export type CrossingActionSlot = 'primary' | 'secondary' | 'next' | 'in_screen' | 'exit';
export type CrossingPillState = 'current' | 'done' | 'upcoming' | 'skipped' | 'skipped-ready';

// Where an action leads: a screen, a rule that picks one of its candidates, nowhere (a Stay), or out of the frame.
export type CrossingActionTarget =
  | { screen: string }
  | { rule: string; candidates: string[] }
  | { stay: true }
  | { exit: string };

export interface CrossingPill {
  number: number;
  name: string;
  state: CrossingPillState;
  stateLabel: string; // the spoken form of state
}

export interface CrossingProgressAction {
  slot: CrossingActionSlot;
  id: string;
  label: string;
  target: CrossingActionTarget;
}

export interface CrossingProgress {
  screenId: string;
  phaseNumber: number;
  phaseName: string;
  eyebrow: string;
  name: string;
  variant: string | null; // the matched name variant, or null
  showSubLabel: boolean;
  scene: CrossingScene;
  kind: CrossingScreenKind;
  actions: CrossingProgressAction[]; // filtered by mode; only exits on a closed crossing
  canGoBack: boolean;
  backTo: string | null;
  canGoForward: boolean;
  forwardTo: string | null;
  path: string[];
  pathIndex: number;
  pills: CrossingPill[];
  closed: boolean;
}

// A crossing response that carries its progress record.
export type CrossingWithProgress = CrossingDto & { progress: CrossingProgress };

// One Too Much pick sent whole: its category and the support labels chosen within it.
export interface CrossingTooMuchPick {
  categoryCode: string;
  supports?: string[];
}

// The values a screen may send, keyed by the definition's value field names. Collections replace the whole set;
// breath fields are running totals, never increments.
export interface CrossingActionValues {
  feeling?: string;
  storm?: string;
  weather?: string;
  reaches?: string[];
  tooMuchCategories?: CrossingTooMuchPick[];
  breathSecondsTotal?: number;
  breathRoundCount?: number;
}

// Body of POST .../actions. action is an action id from progress.actions, or 'back', or 'forward'.
export interface CrossingActionRequest {
  screen: string;
  pathIndex: number;
  action: string;
  values?: CrossingActionValues;
}

// The error codes of a refused action call. A guard on the definition may add its own code to the bad-request set.
export type CrossingActionErrorCode =
  | 'invalid_action'
  | 'invalid_values'
  | 'repair_pending'
  | 'stale_screen'
  | 'crossing_closed';

// 400 (invalid_action, repair_pending) and 409 (stale_screen, crossing_closed): the crossing as it now stands rides along.
export interface CrossingActionErrorBody {
  error: CrossingActionErrorCode;
  message?: string;
  crossing: CrossingDto;
}

// 400 invalid_values: fields names each field that was not accepted.
export interface CrossingActionInvalidValuesBody extends CrossingActionErrorBody {
  error: 'invalid_values';
  fields: string[];
}

// 404: the feature flag is off, the crossing is not found, not owned, or the child token is bad.
export interface CrossingActionNotFoundBody {
  message: string;
}

// Per-child Crossing Steersman preference (Issue #1940). steersmanEnabled is the effective value
// (override ?? seenCount < threshold); mirrored later by #1942's compass count.
export interface CrossingChildPreferencesDto {
  steersmanEnabled: boolean;
  steersmanSeenCount: number;
}

/**
 * Paginated behavior records response (Issue #1081)
 * Returned by /api/Behavior/family/{familyId} with pagination
 */
export interface PagedBehaviorResponse {
  data: BehaviorRecord[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// Goal pagination types (Issue #1426)
export interface GoalStatusCounts {
  open: number;
  completed: number;
  celebrated: number;
  failed: number;
}

export interface PagedGoalResponse {
  data: Goal[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  statusCounts: GoalStatusCounts;
}

export interface RecordBehaviorRequest {
  childId: number;
  type: BehaviorType;  // API expects 'type', not 'behaviorType'
  notes?: string;
}

/**
 * Daily progress summary for a child
 * 
 * This is the core data structure for tracking daily behavior.
 * One record exists per child per day.
 * 
 * LADDER POSITIONS:
 * - 1 = Ready (starting position)
 * - 2 = Good Job  
 * - 3 = Great Job
 * - 4 = Outstanding (earns heart)
 */
export interface DailyProgress {
  progressId: number;
  childId: number;
  date: string; // Date in ISO format (YYYY-MM-DD)
  currentPosition: number; // Current ladder position (1-7)
  currentPositionDisplayName?: string; // Display name for current position
  allPositionDisplayNames?: string[]; // All 7 position display names from API
  earnedHeart: boolean; // True if reached Outstanding position
  onTrackForHeartToday?: boolean; // Live, chore-free "on track" projection (issue #1909)
  starCount: number; // Total stars recorded for the day
  triangleCount: number; // Total triangles recorded for the day
  squareCount: number; // Total squares recorded for the day
  flipYourCoinCount: number; // Total flip your coin behaviors recorded for the day
  makeItRightCount: number; // Total make it right behaviors recorded for the day
  oneOnOneTimeCount: number; // Total 1:1 time sessions recorded for the day
  requiredChoreListsCompleted?: number; // How many required chore lists were completed for the day
  requiredChoreListsTotal?: number; // Total required chore lists for the day
  noHeartReasons?: string[] | null; // Reasons heart was not earned; null or absent if heart was earned
  isAway?: boolean; // True if this is a family away day (issue #1706)
}

/**
 * Per-date manual override for a family's away schedule (issue #1706).
 */
export interface FamilyAwayOverride {
  date: string; // yyyy-MM-dd
  isAway: boolean;
}

/**
 * Family away schedule: recurring weekly rules + per-date overrides (issue #1706).
 */
export interface FamilyAwaySchedule {
  recurringDaysOfWeek: number[]; // 0=Sunday … 6=Saturday
  overrides: FamilyAwayOverride[];
}

/**
 * Goal progress update included in behavior recording responses.
 * Only included for star behaviors (Issue #1414).
 */
export interface GoalProgressUpdate {
  goalId: number;
  currentProgress: number;
  targetValue: number;
  status: GoalStatus;
}

export interface LadderPositionUpdate {
  childId: number;
  oldPosition: number;
  newPosition: number;
  oldPositionDisplayName: string;
  newPositionDisplayName: string;
  positionChange: number;
  heartsEarned: number;
  newHeartStreak: number;
  ladderReset: boolean;
  currentStarCount: number;
  currentTriangleCount: number;
  currentSquareCount: number;
  currentFlipYourCoinCount: number;
  currentMakeItRightCount: number;
  currentOneOnOneTimeCount: number;
  /** Updated goal progress after recording a star. Only present for star behaviors. */
  updatedGoals?: GoalProgressUpdate[];
  /** Server-generated message describing the ladder position change. */
  message?: string;
  /** True when a square was automatically created because a triangle dropped the child to StopAndReset. Issue #1441. */
  autoSquareTriggered?: boolean;
}

/**
 * Information about a goal that was completed by a behavior recording
 * Issue #1415: Goal completion celebration data
 */
export interface CompletedGoalInfo {
  goalId: number;
  childId: number;
  goalTitle: string;
  rewardText: string;
  goalType: number;
  currentValue: number;
  targetValue: number;
  dayPasses: number;
  dayPassesUsed: number;
  daysRemaining: number | null;
  endDate: string | null;
  isRecurring: boolean;
  startDate: string;
}

/**
 * Response from recording a behavior
 * Issue #1415: Extended to include newly completed goals for celebration
 */
export interface RecordBehaviorResponse {
  behavior: BehaviorRecord;
  ladderUpdate: LadderPositionUpdate;
  newlyCompletedGoals?: CompletedGoalInfo[];
  /** Set only when a parent-recorded Reset produced a crossing (Issue #2012). */
  crossingId?: number | null;
}

export enum GoalType {
  HeartStreak = 1,
  StarCollection = 2,
  ResponsibilitiesCompleted = 3,  // Count approved regular chore lists within time period
  HeartCollection = 4             // Goal type for collecting total hearts over a time period
}

export enum GoalStatus {
  Active = 1,
  Completed = 2,
  Failed = 3,
  Celebrated = 4,
}

export interface Goal {
  id: number;
  childId: number;
  familyId: number;
  childName?: string;
  title: string;
  type: GoalType;
  targetValue: number;
  currentProgress?: number;
  progress?: { currentValue?: number }; // Backend nested progress object from GoalDto
  reward?: string;            // Frontend field name (legacy/convenience)
  rewardDescription?: string; // Backend field name (primary)
  rewardImageUrl?: string;    // Reward image data URL
  dayPasses?: number; // Issue #486: Day passes for HeartStreak goals
  dayPassesUsed?: number; // Issue #1309: Day passes used count
  daysRemaining?: number | null; // Issue #1309: Days until goal end date
  endDate?: string | null; // Issue #1309: Goal end date
  isRecurring?: boolean;      // Issue #431: Whether goal automatically recreates when completed/failed
  createdAt: string;
  updatedAt: string;          // Added for celebration sorting
  completedAt?: string;
  celebratedAt?: string;
  failedAt?: string;
  startDate?: string | null;
  totalSquares?: number;  // For heart collection goals - tracks squares used during goal period
  status: GoalStatus;
  currentStreak: number;      // Current streak for heart streak goals
  isDeleted: boolean;         // Soft delete flag
  createdByUserId: number;    // User who created the goal
  isNukeExempt?: boolean;     // Exempt from nuclear reset (Issue #1310)
  durationDays?: number | null; // Issue #1613: Canonical duration in days. Null means no time limit.
}

export interface HeartStreakTemplate {
  consecutiveDays: number;
  dayPasses: number;
}

export interface StarCollectionTemplate {
  totalStars: number;
  timePeriodDays: number;
}

export interface HeartCollectionTemplate {
  totalHearts: number;
  timePeriodDays: number;
}

export interface ResponsibilitiesCompletedTemplate {
  totalLists: number;
  timePeriodDays: number;
}

export interface CreateGoalRequest {
  childId?: number;  // Optional for backward compatibility
  childIds?: number[];  // New multi-child support
  title: string;
  type: GoalType;
  targetValue: number;
  reward?: string;            // Frontend field name (maps to rewardDescription in API)
  rewardDescription?: string; // Backend field name (primary)
  rewardImageUrl?: string;    // Reward image data URL
  dayPasses?: number; // Issue #486: Day passes for HeartStreak goals
  isRecurring?: boolean;      // Issue #431: Whether goal automatically recreates when completed/failed
  isNukeExempt?: boolean;     // Exempt from nuclear reset (Issue #1310)
  // Template-specific fields for dynamic form generation
  heartStreakTemplate?: HeartStreakTemplate;
  starCollectionTemplate?: StarCollectionTemplate;
  heartCollectionTemplate?: HeartCollectionTemplate;
  responsibilitiesCompletedTemplate?: ResponsibilitiesCompletedTemplate;
}

/**
 * Response from nuclear reset (Issue #1310)
 */
export interface NukeResult {
  childId: number;
  nukedGoalIds: number[];
  exemptGoalIds: number[];
  behaviorRecordId: number;
  message: string;
}

/**
 * Child Dashboard data for anonymous child access
 */
export interface ChildDashboard {
  childId: number;
  childName: string;
  starCount: number;
  triangleCount: number;
  squareCount: number;
  flipYourCoinCount?: number;
  oneOnOneTimeCount?: number;
  makeItRightCount?: number;
  ladderPosition: number;
  activeGoals: Goal[];
  todaysBehaviors: BehaviorRecord[];
  familyTimezone?: string;
  child?: Child;
  familyChildIndex?: number; // Issue #1575: Index in family ordered list, used for color assignment
}

/**
 * Child activity tracking for analytics
 */
export interface ChildActivity {
  activityType: string;
  timestamp: string;
  additionalData?: string;
}

// Enhanced Dashboard Types for Issue #108

/**
 * Today's behavior counts for a child
 */
export interface TodaysBehaviorCounts {
  starCount: number;
  triangleCount: number;
  squareCount: number;
  flipYourCoinCount: number;
  makeItRightCount: number;
  oneOnOneTimeCount: number;
  resetCount: number;
}

/**
 * Ladder position information including current position and all available positions
 */
export interface LadderPosition {
  currentPosition: number;
  currentPositionDisplayName: string;
  allPositions: number[];
}

/**
 * Progress icon for goal visualization (hearts, stars, squares)
 */
export interface ProgressIcon {
  iconType: string; // "heart", "star", "square"
  isFilled: boolean;
  color: string; // "red", "yellow", "black"
}

/**
 * Goal progress indicator with visual icons
 */
export interface GoalProgressIndicator {
  goalId: number;
  goalTitle: string;
  rewardText: string;
  rewardImageUrl?: string;
  goalType: number; // GoalType enum
  currentValue: number;
  targetValue: number;
  dayPasses: number;
  dayPassesUsed: number;
  daysRemaining?: number | null;
  endDate?: string;
  progressIcons: ProgressIcon[];
  progressText: string; // e.g., "2/5"
  isRecurring?: boolean; // Issue #578: Recurring goal indicator
  isNukeExempt?: boolean; // Issue #1310: Exempt from nuclear reset
  startDate?: string; // Issue #1415: Goal start date for celebration context
  completedAt?: string; // Issue #1421: When the goal was completed
  failedAt?: string; // Issue #1421: When the goal failed (expired or marked impossible)
  celebratedAt?: string; // Issue #1421: When the goal was celebrated
}

/**
 * Enhanced child dashboard data with context for behavior decisions
 */
export interface EnhancedChildDashboard {
  childId: number;
  name: string;
  aliases?: string[];
  profileImageUrl?: string;
  todaysBehaviorCounts: TodaysBehaviorCounts;
  ladderPosition: LadderPosition;
  activeGoals: GoalProgressIndicator[];
  hasCalendarSelections?: boolean;
  choreStatuses: ChoreListPeriodStatus[];
  onTrackForHeartToday?: boolean;
}

/**
 * Enhanced family dashboard with contextual information for all children
 */
export interface EnhancedFamilyDashboard {
  familyId: number;
  familyName: string;
  children: EnhancedChildDashboard[];
  lastUpdated: string;
}

// Avatar Management Types - Issue #137

/**
 * Response from child avatar upload API
 */
export interface ChildAvatarUploadResponse {
  avatarUrl: string;
}

/**
 * Response from child avatar delete API
 */
export interface ChildAvatarDeleteResponse {
  message: string;
}

/**
 * Avatar upload request payload
 */
export interface AvatarUploadRequest {
  imageData: string; // Base64 encoded image data
}

/**
 * Role suggestion for family member roles
 */
export interface RoleSuggestion {
  id: number;
  roleName: string;
  isActive: boolean;
  isPriority: boolean;
  displayOrder: number;
  createdAt: string;
}

/**
 * Request to create a new role suggestion (admin only)
 */
export interface CreateRoleSuggestionRequest {
  roleName: string;
  isActive?: boolean;
  isPriority?: boolean;
  displayOrder?: number;
}

/**
 * Request to update a role suggestion (admin only)
 */
export interface UpdateRoleSuggestionRequest {
  roleName: string;
  isActive: boolean;
  isPriority: boolean;
  displayOrder: number;
}

/**
 * Request to update a user's role in a family
 */
export interface UpdateUserRoleRequest {
  role: string;
}

/**
 * Policy document types
 */
export type PolicyDocumentType = 'PrivacyPolicy' | 'TermsOfService';

/**
 * Policy document structure
 */
export interface PolicyDocument {
  id: number;
  documentType: PolicyDocumentType;
  content: string;
  version: number;
  versionLabel?: string;
  lastModified: string;
  modifiedByUserId: number;
  modifiedByUserName: string;
}

/**
 * Policy document history entry
 */
export interface PolicyDocumentHistory {
  id: number;
  documentId: number;
  documentType: PolicyDocumentType;
  content: string;
  version: number;
  versionLabel?: string;
  changeReason?: string;
  createdAt: string;
  createdByUserId: number;
  createdByUserName: string;
}

/**
 * Request to update a policy document
 */
export interface UpdatePolicyRequest {
  content: string;
  versionLabel?: string;
  changeReason?: string;
  sendNotification?: boolean;
}

/**
 * Request to acknowledge a policy document
 */
export interface PolicyAcknowledgmentRequest {
  documentType: PolicyDocumentType;
  documentVersion: number;
  ipAddress?: string;
}

/**
 * User policy acknowledgment record
 */
export interface UserPolicyAcknowledgment {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  documentType: PolicyDocumentType;
  documentVersion: number;
  acknowledgedAt: string;
  ipAddress?: string;
}

/**
 * Paginated result for policy acknowledgments
 */
export interface PaginatedPolicyAcknowledgments {
  items: UserPolicyAcknowledgment[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Developmental Averages DTO for analytics comparison features
 */
export interface DevelopmentalAverageDto {
  id: number;
  ageGroupStart: number;
  ageGroupEnd: number;
  behaviorType: number; // BehaviorType enum value (1-5)
  averagePerDay: number | null;
}

/**
 * Alexa Integration Status for family-level voice command management
 * @deprecated Use UserVoiceIntegrationDto and FamilyVoiceIntegrationStatusDto
 */
export interface AlexaIntegrationStatusDto {
  isConnected: boolean;
  familyName?: string;
  lastConnected?: string;
  linkedAt?: string;
  linkedByUserId?: number;
  linkedByUserName?: string;
}

/**
 * User-level voice assistant integration (e.g., Alexa account linked to this user)
 */
export interface UserVoiceIntegrationDto {
  id: number;
  integrationType: string;
  isLinked: boolean;
  linkedAt?: string;
  familyConnections: VoiceIntegrationFamilyConnectionDto[];
}

/**
 * A family that a user's voice integration is connected to
 */
export interface VoiceIntegrationFamilyConnectionDto {
  familyId: number;
  familyName: string;
  connectedAt: string;
}

/**
 * Status of a voice integration connected to a family
 */
export interface FamilyVoiceIntegrationStatusDto {
  userVoiceIntegrationId: number;
  integrationType: string;
  linkedByUserId: number;
  linkedByUserName: string;
  connectedAt: string;
}

/**
 * Family deletion request payload
 */
export interface AdminFamilyDeleteRequest {
  deleteType: 'soft' | 'hard';
  confirmationText: string;
}

/**
 * Family deletion summary details
 */
export interface FamilyDeletionSummary {
  familyName: string;
  childrenDeleted: number;
  adultsRemoved: number;
  adultsDeleted: number;
  goalsDeleted: number;
  behaviorLogsDeleted: number;
  imagesDeleted: number;
  childAccessTokensDeleted: number;
  refreshTokensDeleted: number;
  passwordResetTokensDeleted: number;
  userPolicyAcknowledgmentsDeleted: number;
  oAuthCodesDeleted: number;
}

/**
 * Family deletion response
 */
export interface AdminFamilyDeletionResponse {
  success: boolean;
  deletionSummary: FamilyDeletionSummary;
}

/**
 * User authentication provider information
 */
export interface UserAuthProvider {
  id: number;
  authProvider: string;
  externalId?: string;
  createdAt: string;
  canRemove: boolean;
}

/**
 * Request to remove an authentication provider
 */
export interface RemoveAuthProviderRequest {
  authProvider: string;
}

/**
 * Response from checking if an auth provider can be removed
 */
export interface CanRemoveAuthProviderResponse {
  canRemove: boolean;
}

/**
 * Recording streak analytics data for family progress tracking
 */
export interface RecordingStreakDto {
  currentStreak: number;
  bestStreak: number;
  streakMessage: string;
  fireIntensity: string;
  userActiveDaysLast7: number;
  showBestStreak: boolean;
}

/**
 * Family analytics response structure
 */
export interface FamilyAnalyticsResponse {
  timeWindow: {
    startDate: string;
    endDate: string;
    timezone: string;
    label: string;
  };
  metrics: {
    adultsLogging?: Array<{
      userId: number;
      name: string;
      profileImageUrl?: string;
      behaviorCount: number;
    }>;
    recordingStreak?: RecordingStreakDto;
  };
}

/**
 * Subscription status types from LemonSqueezy integration spec
 */
export type SubscriptionStatus =
  | 'none'         // No subscription yet (Issue #1150)
  | 'trial'        // Active trial period
  | 'active'       // Active paid subscription
  | 'cancelled'    // Cancelled but still in grace period
  | 'expired'      // Trial expired or grace period ended
  | 'past_due'     // Payment failed
  | 'refunded'     // Refund processed, access revoked
  | 'free';        // Admin-granted free account

/**
 * Subscription status response from API (matches backend SubscriptionStatusDto)
 * Issue #1026 & #1036 - Combined interface with all fields
 */
export interface SubscriptionStatusResponse {
  id: number;
  familyId: number;
  lemonSqueezySubscriptionId?: string;
  status: SubscriptionStatus;
  planName: string;
  billingOwnerUserId?: number;
  trialStartsAt?: string;
  trialEndsAt?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelledAt?: string;
  isFreeAccount: boolean;
  freeAccountExpiresAt?: string;
  isLockedOut: boolean;
  lockedOutReason?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  // Issue #1026 - Additional computed fields for ribbon display
  daysRemaining?: number;
  isPrimaryCaregiver: boolean;
}

/**
 * Create checkout session request (Issue #1030)
 */
export interface CreateCheckoutRequest {
  email: string;
}

/**
 * Create checkout session response (Issue #1030)
 */
export interface CreateCheckoutResponse {
  url: string;
}

// ============================================================================
// CHORES TYPES (Issue #1308)
// ============================================================================

/**
 * Repeat frequency for chore lists
 */
export type RepeatFrequency = 'Daily' | 'Weekly' | 'Monthly';

/**
 * Chore list type - determines creation source and approval workflow
 *
 * MAKE IT RIGHT WORKFLOW:
 * - Regular: Parent-created chore lists with standard workflow
 * - MakeItRight: Auto-generated from square behaviors, requires parent approval before completion awards stars
 */
export enum ChoreListType {
  Regular = 0,           // Parent-created chore lists
  MakeItRight = 1,       // Auto-created from square behaviors requiring approval
  ResetAndReflect = 2,   // Auto-created from FlipYourCoin behaviors requiring parent approval
  OneOnOneTime = 3       // Auto-created from OneOnOneTime behaviors requiring parent approval
}

/**
 * Completion/finalization state for a chore list's current period.
 *
 * STATE MACHINE:
 * - Incomplete: not all items are done yet
 * - PendingApproval: all items done, awaiting parent approval (RequiresApproval lists only)
 * - Finalized: parent has approved (or approval wasn't required), star awards triggered
 */
export enum ChoreListStatus {
  Incomplete = 0,
  PendingApproval = 1,
  Finalized = 2,
}

/**
 * Chore item within a chore list
 */
export interface ChoreItem {
  id: number;
  choreListId: number;
  title: string;
  sortOrder: number;
  isCompleted: boolean;
  completedAt?: string;
  completedByUserId?: number;
  completedByUserName?: string;
}

/**
 * Chore item status for current period
 */
export interface ChoreItemStatus {
  id: number;
  title: string;
  sortOrder: number;
  isCompleted: boolean;
  completedAt?: string;
}

/**
 * Chore list with items
 */
export interface ChoreList {
  id: number;
  childId: number;
  childName: string;
  title: string;
  notes?: string;
  startDate: string;
  repeatFrequency: RepeatFrequency;
  requiredForHeart: boolean;
  type: ChoreListType;
  sortOrder: number;
  requiresApproval: boolean;
  status: ChoreListStatus;
  completedAt?: string;
  finalizedAt?: string;
  finalizedByUserId?: number;
  createdAt: string;
  updatedAt: string;
  createdByUserId: number;
  items: ChoreItem[];
  totalItems: number;
  completedItems: number;
  isComplete: boolean;
  /** Bitmask of active days (Sun=1,Mon=2,Tue=4,Wed=8,Thu=16,Fri=32,Sat=64). Only for Daily lists. */
  daysOfWeek: number;
  /** True if a parent has skipped this list for today. Only for Daily lists. */
  isSkippedToday: boolean;
  /** The last day this list is active (inclusive, YYYY-MM-DD). Null means active indefinitely. */
  endDate?: string | null;
}

/**
 * Make It Right template for a child
 */
export interface MakeItRightTemplate {
  id: number;
  childId: number;
  tasks: string[];
}

/**
 * Request to update Make It Right template
 */
export interface UpdateMakeItRightTemplateRequest {
  tasks: string[];
}

/**
 * Site default Make It Right tasks (admin only)
 */
export interface SiteDefaultMakeItRightTasks {
  tasks: string[];
}

/**
 * Reset and Reflect template for a child
 */
export interface ResetAndReflectTemplate {
  id: number;
  childId: number;
  tasks: string[];
}

/**
 * Request to update Reset and Reflect template
 */
export interface UpdateResetAndReflectTemplateRequest {
  tasks: string[];
}

/**
 * Site default Reset and Reflect tasks (admin only)
 */
export interface SiteDefaultResetAndReflectTasks {
  tasks: string[];
}

/**
 * Per-child 1:1 Time template
 */
export interface OneOnOneTimeTemplate {
  id: number;
  childId: number;
  tasks: string[];
}

/**
 * Request to update 1:1 Time template
 */
export interface UpdateOneOnOneTimeTemplateRequest {
  tasks: string[];
}

/**
 * Site default 1:1 Time tasks (admin only)
 */
export interface SiteDefaultOneOnOneTimeTasks {
  tasks: string[];
}

/**
 * Chore list with current period status
 */
export interface ChoreListPeriodStatus {
  id: number;
  title: string;
  repeatFrequency: RepeatFrequency;
  requiredForHeart: boolean;
  periodStart: string;
  periodEnd: string;
  totalItems: number;
  completedItems: number;
  isComplete: boolean;
  items: ChoreItemStatus[];
  type: ChoreListType;
  requiresApproval: boolean;
  status: ChoreListStatus;
  completedAt?: string;
  finalizedAt?: string;
  finalizedByUserId?: number;
  // startDate/sortOrder/createdAt (issue #1916): mirror the backend's chore list ordering rule
  // client-side, and compute the grace-period delay before a newly-created pinned list is
  // allowed to visibly reorder the dashboard. Optional because older test fixtures and the
  // getChoreListStatuses fallback shape predate these fields — real API responses always send them.
  startDate?: string;
  sortOrder?: number;
  createdAt?: string;
}

/**
 * Request to create a new chore item
 */
export interface CreateChoreItemRequest {
  title: string;
  sortOrder: number;
}

/**
 * Request to create a new chore list
 */
export interface CreateChoreListRequest {
  childId: number;
  title: string;
  notes?: string;
  startDate: string;
  repeatFrequency: RepeatFrequency;
  requiredForHeart: boolean;
  requiresApproval: boolean;
  /** Bitmask of active days (1–127). Only used for Daily lists. Default 127 = all days. */
  daysOfWeek?: number;
  items: CreateChoreItemRequest[];
}

/**
 * Request to update a chore item
 */
export interface UpdateChoreItemRequest {
  id?: number;
  title: string;
  sortOrder: number;
}

/**
 * Request to update a chore list
 */
export interface UpdateChoreListRequest {
  title: string;
  notes?: string;
  requiredForHeart?: boolean;
  requiresApproval?: boolean;
  /** Bitmask of active days (1–127). Only used for Daily lists. Null = no change. */
  daysOfWeek?: number;
  items?: UpdateChoreItemRequest[];
}

/**
 * Completion history entry
 */
export interface ChoreCompletion {
  id: number;
  choreItemId: number;
  choreItemTitle: string;
  completedAt: string;
  completedByUserId?: number;
  completedByUserName?: string;
  periodStart: string;
}

/**
 * Paginated chore history response
 */
export interface ChoreHistoryResponse {
  items: ChoreCompletion[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Child chores summary for dashboard
 */
export interface ChildChoresSummary {
  childId: number;
  childName: string;
  totalChoreLists: number;
  completedChoreLists: number;
  requiredForHeartCount: number;
  requiredForHeartCompleted: number;
  allRequiredChoresComplete: boolean;
}

/**
 * Family-wide chores summary
 */
export interface FamilyChoresSummary {
  familyId: number;
  children: ChildChoresSummary[];
}

/**
 * Response from marking a chore item as complete
 * Includes information about star awards (Issue #1343)
 */
export interface MarkItemCompleteResponse {
  message: string;
  starAwarded: boolean;
  choreListId?: number;
  choreListTitle?: string;
  listPendingApproval?: boolean;
}

// Calendar Integration Types

export enum CalendarProviderType {
  Google = 1,
  Outlook = 2,
  CalDAV = 3,
  IcsSubscription = 4,
}

export interface CalendarConnectionDto {
  id: number;
  familyId: number;
  providerType: CalendarProviderType;
  displayName: string;
  externalAccountId?: string;
  calendarSourceCount: number;
  isHealthy: boolean;
  createdAt: string;
  calendarSources: CalendarSourceDto[];
}

export interface CalendarSourceDto {
  id: number;
  calendarConnectionId: number;
  externalCalendarId: string;
  displayName: string;
}

export interface CreateCalendarConnectionDto {
  providerType: CalendarProviderType;
  displayName?: string;
  authorizationCode?: string;
  redirectUri?: string;
  serverUrl?: string;
  username?: string;
  password?: string;
}

export interface CalendarEventDto {
  title: string;
  startTime: string;
  endTime: string;
  calendarSourceId: number;
  isAllDay: boolean;
}

export interface ChildCalendarEventsDto {
  childId: number;
  date: string;
  events: CalendarEventDto[];
  allDayEvents?: CalendarEventDto[]; // undefined if absent (old API), [] if no events
  isStale: boolean;
  errorMessage?: string;
}

export interface ChildCalendarSelectionDto {
  childId: number;
  selectedCalendars: CalendarSourceDto[];
}

export interface CalendarOAuthStartDto {
  authorizationUrl: string;
  state: string;
}

export interface CalendarConnectionHealthDto {
  connectionId: number;
  isHealthy: boolean;
  requiresReauth?: boolean;
  errorMessage?: string;
}

export interface UpdateCalDavCredentialsDto {
  serverUrl: string;
  username: string;
  password: string;
  displayName?: string;
}

/**
 * Raw behavior record as returned by the API before frontend transformation.
 * The API uses 'id' and 'type' while the frontend BehaviorRecord uses 'behaviorId' and 'behaviorType'.
 */
export interface RawBehaviorDto {
  id?: number;
  behaviorId?: number;
  childId: number;
  type?: number;
  behaviorType?: number;
  notes?: string;
  recordedAt?: string;
  createdAt?: string;
  recordedByUserId?: number;
  recordedByUserName?: string;
  source?: number;
  sourceDisplayName?: string;
  eventTime?: string;
  eventTimezone?: string;
  eventLocalTime?: string;
}

/**
 * Raw family detail DTO as returned by /api/family/{id}.
 * Field names differ from the frontend Family type and are transformed by getFamilyDetails.
 */
export interface FamilyDetailDto {
  id: number;
  name: string;
  description?: string;
  timezone?: string;
  weekStartDay?: number;
  createdByUserId: number;
  createdByUserName?: string;
  createdAt: string;
  members?: FamilyMemberDto[];
  children?: Child[];
  effectiveDailyLoggingGoalPerChild?: number;
  themeName?: string;
  effectiveThemeName?: string;
  labelLanguageMode?: string;
  effectiveLabelLanguageMode?: string;
}

/**
 * Family member as returned by /api/family/{id}/members
 */
export interface FamilyMemberDto {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl?: string;
  role?: number;
  customRole?: string;
  joinedAt: string;
  isBillingOwner?: boolean;
}

/**
 * Invitation DTO as returned by /api/family/{id}/invite and /api/family/{id}/invitations
 */
export interface InvitationDto {
  id: number;
  familyName?: string;
  invitedByName: string;
  email: string;
  role: number;
  customRole?: string;
  message?: string;
  firstName?: string;
  lastName?: string;
  invitedAt: string;
  expiresAt?: string;
  status?: string;
  token?: string;
}

/**
 * Child access token DTO as returned by /api/family/{id}/child-access-tokens
 */
export interface ChildAccessTokenDto {
  childId: number;
  childName: string;
  accessUrl: string;
  isActive: boolean;
  createdAt: string;
}

/**
 * Generic paged API response used by admin and other paged endpoints.
 * The backend returns this shape for all paginated collections.
 */
export interface PagedApiResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Child dashboard data returned by /api/child-dashboard/{urlSlug}
 */
export interface ChildDashboardDto {
  childId: number;
  childName: string;
  starCount: number;
  triangleCount: number;
  squareCount: number;
  ladderPosition: number;
  activeGoals: Array<{
    id: number;
    title: string;
    description?: string;
    targetValue: number;
    currentProgress: number;
    goalType: string;
    status: number;
    isRecurring: boolean;
    celebratedAt?: string;
    completedAt?: string;
    rewardDescription?: string;
  }>;
  todaysBehaviors: RawBehaviorDto[];
  familyTimezone?: string;
  familyChildIndex?: number;
}

/**
 * Meltdown tracker response from /api/behavior/meltdown-tracker/child/{id}
 */
export interface MeltdownTrackerDto {
  childId: number;
  childName: string;
  childAge: number;
  weekStartDay: string;
  currentWeek: {
    startDate: string;
    endDate: string;
    meltdownCount: number;
    daysIntoWeek: number;
  };
  historicalAverage: number;
  developmentalRanges: Array<{
    ageGroupStart: number;
    ageGroupEnd: number;
    label: string;
    minPerWeek: number;
    maxPerWeek: number;
    isCurrentAge: boolean;
  }>;
}

/**
 * Triangle (struggles) tracker response from /api/behavior/triangle-tracker/child/{id}
 */
export interface TriangleTrackerDto {
  childId: number;
  childName: string;
  childAge: number;
  today: {
    date: string;
    triangleCount: number;
  };
  historicalAverage: number;
  developmentalRanges: Array<{
    ageGroupStart: number;
    ageGroupEnd: number;
    label: string;
    minPerDay: number;
    maxPerDay: number;
    isCurrentAge: boolean;
  }>;
}

/**
 * One-on-one time tracker response from /api/behavior/one-on-one-time-tracker/child/{id}
 */
export interface OneOnOneTimeTrackerDto {
  childId: number;
  childName: string;
  childAge: number;
  weekStartDay: string;
  currentWeek: {
    startDate: string;
    endDate: string;
    daysWithSessions: number;
    daysIntoWeek: number;
  };
  historicalAverage: number;
  developmentalRanges: Array<{
    ageGroupStart: number;
    ageGroupEnd: number;
    label: string;
    minPerWeek: number;
    maxPerWeek: number;
    isCurrentAge: boolean;
  }>;
}

/**
 * Result DTO from admin family reprocess/recalculate-goals endpoint
 */
export interface GoalRecalculationResultDto {
  success: boolean;
  familiesProcessed: number;
  familiesWithErrors: number;
  totalGoalsRecalculated: number;
  batchesProcessed: number;
  celebratedGoalsSkipped: number;
  heartsFixed: number;
  dailyProgressRecordsUpdated: number;
  processingStartTime: string;
  processingEndTime: string;
  processingDuration: string;
  validationPassed: boolean;
  validationErrors: string[];
  errors: string[];
  goalTypesProcessed: number[];
}

// Crossing content library (GET /api/crossing/content) — site-wide copy, Issue #1938
export interface CrossingTooMuchSupportDto {
  code: string;
  label: string;
}

export interface CrossingTooMuchCategoryDto {
  code: string;
  title: string;
  icon: string | null;
  phrase: string | null;
  oceanLine: string;
  supports: CrossingTooMuchSupportDto[];
}

export interface CrossingWeatherPatternDto {
  code: string;
  name: string;
  checkQuestion: string;
  sentence: string;
  verb: string;
  sceneDescription: string;
  noStormFeltTemplate: string;
}

export interface CrossingStormDto {
  code: string;
  name: string;
  plainPhrase: string;
  houseName: string | null;
  epithet: string | null;
  truth: string | null;
  childTruth: string | null;
  rule: string | null;
  correction: string | null;
  truthTiers: string[];
}

export interface CrossingReachMendOptionDto {
  code: string;
  label: string;
}

export interface CrossingReachTypeDto {
  code: string;
  name: string;
  shortLabel: string;
  icon: string;
  clues: string;
  question: string;
  readBack: string;
  mendOptions: CrossingReachMendOptionDto[];
}

export interface CrossingFeelingDto {
  code: string;
  name: string;
  question: string;
  sentence: string;
}

export interface CrossingSteersmanTipDto {
  screenId: string;
  text: string;
}

export interface CrossingContentDto {
  tooMuchCategories: CrossingTooMuchCategoryDto[];
  weatherPatterns: CrossingWeatherPatternDto[];
  storms: CrossingStormDto[];
  reachTypes: CrossingReachTypeDto[];
  feelings: CrossingFeelingDto[];
  steersmanTips: CrossingSteersmanTipDto[];
}
